import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { identityGateway } from '../services';
import { AuthShellLayout } from '../components/AuthShellLayout';
import { TextField } from '../../../shared/components/TextField';
import { Button } from '../../../shared/components/Button';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Backend's own enumeration-safe copy (`AccountUnlockEndpoints.UnlockRequestAcceptedMessage`) — never
 * distinguishes "no such account" from "that account isn't locked." */
const ACCEPTED_MESSAGE = "If that account is locked, we've sent instructions to unlock it.";

/**
 * Standalone route (`/unlock-request`) for the same action LoginPage's own Account Locked view already
 * offers inline — reachable directly (e.g. a bookmark, or a visitor who closed the Locked view) as well
 * as from there. Mirrors ForgotPasswordPage.tsx's shape almost exactly, per the same enumeration-safety
 * contract.
 */
export function UnlockRequestPage() {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFieldError('Enter your email address.');
      return;
    }
    if (!EMAIL_FORMAT.test(trimmedEmail)) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    try {
      await identityGateway.requestUnlock(trimmedEmail);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err, "We couldn't send that right now."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShellLayout>
      <div className="auth-shell__form-slot">
        <h1>Unlock Your Account</h1>
        {submitted ? (
          <>
            <InlineAlert variant="success" data-testid="unlock-request-success-message">
              {ACCEPTED_MESSAGE}
            </InlineAlert>
            <p className="auth-shell__secondary-action">
              <Link to="/login" className="auth-shell__inline-link">
                Back to Sign In
              </Link>
            </p>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p className="muted">
              If your account is locked after too many failed sign-in attempts, enter your email address
              and we&rsquo;ll send you a link to unlock it.
            </p>

            <TextField
              label="Email Address"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              errorMessage={fieldError}
              data-testid="unlock-request-email-input"
              autoFocus
            />

            {submitError && (
              <InlineAlert variant="error" data-testid="unlock-request-error-message">
                {submitError}
              </InlineAlert>
            )}

            <div className="auth-shell__submit-row">
              <Button
                type="submit"
                loading={submitting}
                loadingLabel="Sending…"
                className="auth-submit-button"
                data-testid="unlock-request-submit-button"
              >
                Send Unlock Link
              </Button>
              <p className="auth-shell__secondary-action">
                <Link to="/login" className="auth-shell__inline-link">
                  Back to Sign In
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </AuthShellLayout>
  );
}
